import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { applyConsent, getStoredConsent, storeConsent } from "@/lib/gtag";

export const CookieBanner = () => {
  const [show, setShow] = useState(false);
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const saved = getStoredConsent();
    if (saved) {
      // returning visitor — re-apply their stored choice to Consent Mode
      applyConsent({ analytics: !!saved.analytics, marketing: !!saved.marketing });
    } else {
      setShow(true);
    }
  }, []);

  const decide = (choice) => {
    applyConsent(choice);
    storeConsent({ essential: true, ...choice });
    setShow(false);
    window.dispatchEvent(new Event("consent-decided"));
  };

  if (!show) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed z-[60] bg-white border-rose-100 shadow-[0_20px_60px_-30px_rgba(159,18,57,0.45)] bottom-0 inset-x-0 border-t p-3 sm:p-6 sm:bottom-6 sm:inset-x-auto sm:right-6 sm:max-w-md sm:border sm:rounded-2xl"
    >
      <div className="flex items-start gap-3">
        <span className="hidden sm:grid place-items-center w-9 h-9 rounded-full bg-rose-50 text-rose-700 shrink-0">
          <Cookie className="w-4 h-4" />
        </span>
        <div className="w-full">
          <p className="text-xs sm:text-sm text-stone-700 leading-relaxed">
            Utilizamos cookies essenciais e, com o seu consentimento, cookies de estatística e de
            marketing (Google Ads). Consulte a{" "}
            <Link to="/politica-de-cookies" className="text-rose-800 underline">
              Política de Cookies
            </Link>.
          </p>

          {custom && (
            <div className="mt-4 space-y-3" data-testid="cookie-custom">
              <label className="flex items-center justify-between gap-3 opacity-70">
                <span className="text-sm text-stone-700">Essenciais <span className="text-xs text-stone-400">(sempre ativos)</span></span>
                <input type="checkbox" checked readOnly className="accent-rose-800 w-4 h-4" />
              </label>
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <span className="text-sm text-stone-700">Estatísticas</span>
                <input type="checkbox" data-testid="cookie-pref-analytics" checked={prefs.analytics}
                  onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                  className="accent-rose-800 w-4 h-4" />
              </label>
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <span className="text-sm text-stone-700">Marketing</span>
                <input type="checkbox" data-testid="cookie-pref-marketing" checked={prefs.marketing}
                  onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                  className="accent-rose-800 w-4 h-4" />
              </label>
              <button
                onClick={() => decide({ analytics: prefs.analytics, marketing: prefs.marketing })}
                data-testid="cookie-save"
                className="btn-rose w-full inline-flex items-center justify-center rounded-full bg-rose-800 text-white px-6 py-2.5 text-sm"
              >
                Guardar escolhas
              </button>
            </div>
          )}

          {!custom && (
            <div className="mt-2.5 sm:mt-4 flex flex-wrap items-center gap-2">
              <button
                onClick={() => decide({ analytics: true, marketing: true })}
                data-testid="cookie-accept-all"
                className="btn-rose inline-flex items-center rounded-full bg-rose-800 text-white px-4 py-2 text-xs sm:text-sm sm:px-5 sm:py-2.5"
              >
                Aceitar todos
              </button>
              <button
                onClick={() => decide({ analytics: false, marketing: false })}
                data-testid="cookie-reject"
                className="inline-flex items-center rounded-full border border-stone-200 text-stone-700 px-4 py-2 text-xs sm:text-sm sm:px-5 sm:py-2.5 hover:border-stone-400 transition-colors"
              >
                Recusar
              </button>
              <button
                onClick={() => setCustom(true)}
                data-testid="cookie-customize"
                className="inline-flex items-center text-xs sm:text-sm text-stone-500 hover:text-stone-800 px-2 py-2 link-underline"
              >
                Personalizar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
